import * as React from 'react';
import { Messages } from '../../codegen/Localize';
import { TabStyles } from '@tableau/widgets';
import { cssClass } from '../../style';
import { ReactActionDialogOptions, openReactActionDialog } from './ReactActionDialog';
import { ModalInstance } from './ReactModalService';
import { TabDialogWidth } from './style/TabDialogWidth';

type ReactActionDialogOptionsForConfirmationDialog<ActionParams, TResult> =
  ReactActionDialogOptions<ReactConfirmationDialogBodyProps, ActionParams, TResult>;

/**
 * Options for the ReactConfirmationDialog, which wraps ReactActionDialog for the most basic use case.
 *
 * See `ReactActionDialogOptions` for a description of the few params inherited from there.
 */
export type ReactConfirmationDialogOptions<ActionParams, TResult> = {

  readonly title?: ReactActionDialogOptionsForConfirmationDialog<ActionParams, TResult>['title'];
  readonly isActionDelete?: ReactActionDialogOptionsForConfirmationDialog<ActionParams, TResult>['isActionDelete'];
  readonly centerActionButtons?: ReactActionDialogOptionsForConfirmationDialog<ActionParams, TResult>['centerActionButtons'];

  /**
   * The message to display in the dialog body
   */
  readonly message: string;

  /**
   * Static data to be passed through to the action. This param can be used in order to write the action
   * function in a way that doesn't depend on closures, which makes the action function easier to unit test.
   */
  readonly paramsForAction: ActionParams;

  /**
   * If specified, renders the dialog with the component given.
   * Defaults to the simple ReactConfirmationDialogStringBody.
   *
   * The `message` option is passed into this component as a prop, although a custom component
   * could choose to ignore it and hardcode its own messages.
   */
  readonly bodyComponent?: React.ComponentType<ReactConfirmationDialogBodyProps>;

  /** The name of the button that confirms the action. */
  readonly actionName: string;

  /**
   * The action to take when the user clicks the Confirm button.
   * - If the action is successful, the promise should be resolved;
   * - if the action is unsuccessful, the promise should be rejected.
   */
  readonly action: (params: ActionParams) => PromiseLike<TResult>;

  /**
   * The dialog width, use TabDialogWidth.width375px as a default
   */
  dialogWidth: TabDialogWidth;
}

/**
 * Opens a "confirmation dialog". This is a wrapper around ReactActionDialog with many props
 * predefined for a simpler use case of a dialog that contains no interactive elements in the dialog body;
 * only body text and the Confirm/Cancel buttons.
 *
 * Like ReactActionDialog, the confirmation dialog returns a modalInstance that holds a promise.
 * - After the action is completed successfully, the promise is resolved,
 * - After the action fails or the user manually dismisses the dialog, the promise is rejected.
 *
 * Dismissing the dialog is a normal occurrence, so the calling code should swallow the promise
 * rejection in most scenarios. If the dialog is being executed from a Redux saga, this swallowing
 * happens automatically if the rejected promise propagates up to the root saga
 * (i.e. you don't have to write your own `catch` block).
 *
 * See `openReactActionDialog` for more information.
 */
export function openReactConfirmationDialog<ActionParams, TResult>(
  options: ReactConfirmationDialogOptions<ActionParams, TResult>
): ModalInstance<TResult> {

  const optionsBodyComponent = options.bodyComponent as
      ReactActionDialogOptionsForConfirmationDialog<ActionParams, TResult>['bodyComponent'];

  const actionDialogOptions: ReactActionDialogOptionsForConfirmationDialog<ActionParams, TResult> = {
    title: options.title,
    bodyComponent: optionsBodyComponent || ReactConfirmationDialogStringBody,
    initialBodyProps: { message: options.message },
    initialDialogState: options.paramsForAction,
    actionName: options.actionName,
    action: (__, params) => options.action(params),
    dialogWidth: options.dialogWidth,
    dismissDialogOnActionFailed: true,
    isActionDelete: options.isActionDelete,
    centerActionButtons: options.centerActionButtons,
  };

  return openReactActionDialog(actionDialogOptions);
}

export interface ReactConfirmationDialogBodyProps {
  readonly message: string;
}

/**
 * The default body for a confirmation dialog. It's not exported, to encourage people to use
 * ReactConfirmationDialog directly, instead of using this body in a ReactActionDialog.
 */
function ReactConfirmationDialogStringBody(props: ReactConfirmationDialogBodyProps): JSX.Element {
  return (
    <div data-tb-test-id={ReactConfirmationDialogBodyTestId}>
      {props.message}
    </div>
  );
}

/**
 * A dialog body that renders the message string below an "Are you sure?" header.
 */
export function ReactConfirmationDialogAreYouSureBody(props: ReactConfirmationDialogBodyProps): JSX.Element {
  return (
    <div className={AreYouSureContainer}>
      <div className={AreYouSureHeader}>
        {Messages.confirmAreYouSure()}
      </div>
      <div data-tb-test-id={ReactConfirmationDialogBodyTestId}>
        {props.message}
      </div>
    </div>
  );
}

export const ReactConfirmationDialogBodyTestId = 'confirmation-dialog-body';

const AreYouSureContainer = cssClass('confirmation-dialog', 'are-you-sure-container', {
  textAlign: 'center',
  marginTop: TabStyles.Spacing.LargeSpace
});

const AreYouSureHeader = cssClass('confirmation-dialog', 'are-you-sure-container', {
  fontWeight: 'bold',
  fontSize: TabStyles.Typography.Sizing.XLarge,
  marginBottom: TabStyles.Spacing.LargeSpace
});
